import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import ParticleBackground from './ParticleBackground';

// 動く波形背景コンポーネント
const AnimatedWaveBackground = () => {
  return (
    <>
      {/* 波形レイヤー1 - 奥行きのある大きな波（右に動く） */}
      <motion.svg
        className="absolute inset-0 w-full h-full opacity-25 max-w-full"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid meet"
        initial={{ x: 0 }}
        animate={{ x: [0, 0, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        style={{ width: '100%', height: 'auto' }}
      >
        <defs>
          <linearGradient id="waveGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.5" />
            <stop offset="30%" stopColor="#06B6D4" stopOpacity="0.7" />
            <stop offset="60%" stopColor="#3B82F6" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="waveGradient1Vertical" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#06B6D4" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {/* 大きな波 - スプラッシュ感のある複雑な波形 */}
        <path
          d="M0,600 Q200,300 400,500 T800,450 T1200,550 T1600,400 T1920,500 L1920,1080 L0,1080 Z"
          fill="url(#waveGradient1)"
        />
        {/* 中サイズの波 - 奥行きを出す */}
        <path
          d="M0,650 Q300,350 600,600 T1200,500 T1920,600 L1920,1080 L0,1080 Z"
          fill="url(#waveGradient1Vertical)"
          opacity="0.7"
        />
        {/* 小さな波 - スプラッシュの細かい動き */}
        <path
          d="M0,700 Q150,500 300,650 Q450,800 600,600 Q750,400 900,550 Q1050,700 1200,500 Q1350,300 1500,450 Q1650,600 1800,500 L1920,550 L1920,1080 L0,1080 Z"
          fill="url(#waveGradient1)"
          opacity="0.5"
        />
      </motion.svg>

      {/* 波形レイヤー2 - 逆方向の波（左に動く、より速く） */}
      <motion.svg
        className="absolute inset-0 w-full h-full opacity-20 max-w-full"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid meet"
        initial={{ x: 0 }}
        animate={{ x: [0, 0, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      >
        <defs>
          <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.4" />
            <stop offset="40%" stopColor="#3B82F6" stopOpacity="0.6" />
            <stop offset="70%" stopColor="#06B6D4" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0891B2" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="waveGradient2Vertical" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.5" />
            <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0891B2" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {/* 大きな波 - 逆方向 */}
        <path
          d="M0,500 Q250,200 500,400 T1000,350 T1500,450 T1920,300 L1920,1080 L0,1080 Z"
          fill="url(#waveGradient2)"
        />
        {/* 中サイズの波 */}
        <path
          d="M0,550 Q400,250 800,500 T1600,450 T1920,550 L1920,1080 L0,1080 Z"
          fill="url(#waveGradient2Vertical)"
          opacity="0.6"
        />
        {/* 小さな波 - スプラッシュ */}
        <path
          d="M0,600 Q200,400 400,550 Q600,700 800,500 Q1000,300 1200,450 Q1400,600 1600,400 Q1800,200 1920,400 L1920,1080 L0,1080 Z"
          fill="url(#waveGradient2)"
          opacity="0.4"
        />
      </motion.svg>

      {/* 波形レイヤー3 - 高速の細かい波（奥行きを強調） */}
      <motion.svg
        className="absolute inset-0 w-full h-full opacity-15 max-w-full"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid meet"
        initial={{ x: 0 }}
        animate={{ x: [0, 0, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      >
        <defs>
          <linearGradient id="waveGradient3" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#06B6D4" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* 細かい波 - スプラッシュの細部 */}
        <path
          d="M0,680 Q100,580 200,650 Q300,720 400,600 Q500,480 600,550 Q700,620 800,500 Q900,380 1000,450 Q1100,520 1200,400 Q1300,280 1400,350 Q1500,420 1600,300 Q1700,180 1800,250 Q1900,320 1920,400 L1920,1080 L0,1080 Z"
          fill="url(#waveGradient3)"
        />
      </motion.svg>

      {/* 幾何学模様レイヤー - グリッド（より速く） */}
      <motion.div
        className="absolute inset-0 opacity-8"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='120' height='120' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 120 0 L 0 0 0 120' fill='none' stroke='%233B82F6' stroke-width='0.5' opacity='0.3'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`,
        }}
        animate={{
          x: [0, 80, 0],
          y: [0, 50, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      {/* 幾何学模様レイヤー - 六角形パターン（より速く回転） */}
      <motion.svg
        className="absolute inset-0 w-full h-full opacity-5"
        viewBox="0 0 1920 1080"
        preserveAspectRatio="xMidYMid slice"
        animate={{
          rotate: [0, 360],
        }}
        transition={{
          duration: 80,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <defs>
          <pattern id="hexPattern" x="0" y="0" width="200" height="173" patternUnits="userSpaceOnUse">
            <polygon
              points="100,0 150,43 150,130 100,173 50,130 50,43"
              fill="none"
              stroke="#3B82F6"
              strokeWidth="1"
              opacity="0.2"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hexPattern)" />
      </motion.svg>

      {/* 円形の波紋エフェクト（より速く、スプラッシュ感） */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-blue-500 opacity-10"
        animate={{
          scale: [1, 1.6, 2.2],
          opacity: [0.12, 0.06, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeOut",
        }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-cyan-500 opacity-10"
        animate={{
          scale: [1, 1.6, 2.2],
          opacity: [0.12, 0.06, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          delay: 1.5,
          ease: "easeOut",
        }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-blue-400 opacity-10"
        animate={{
          scale: [1, 1.6, 2.2],
          opacity: [0.12, 0.06, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          delay: 3,
          ease: "easeOut",
        }}
      />
    </>
  );
};

const Hero = () => {
  const { t } = useTranslation();

  const handleDemoClick = () => {
    // SmartMates for Salesの営業チャットページに遷移（ヒーローボタンからの直接通話）
    // hearing_mode=trueでバナーをスキップし、from_hp=trueでリダイレクト先を指定
    window.location.href = 'https://sales-ai-chat-production.up.railway.app/c/dreamsakura/chat?hearing_mode=true&from_hp=true';
  };

  const handleTextChatClick = () => {
    // テキストチャットを直接開始（from_hp=trueでリダイレクト先を指定）
    window.location.href = 'https://sales-ai-chat-production.up.railway.app/c/dreamsakura/chat?from_hp=true';
  };

  return (
    <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 min-h-screen flex items-center justify-center overflow-hidden w-full overflow-x-clip">
      {/* 背景レイヤー */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* パーティクル背景（宇宙と粒子の波） */}
        <ParticleBackground />
        
        {/* 動く波形・幾何学模様背景 */}
        <AnimatedWaveBackground />
        
        {/* グラデーションオーバーレイ（下層） */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-blue-900/50 to-slate-900/60 z-10"></div>
        
        {/* 背景画像（グラデーションの上、前面）- スマホでは非表示 */}
        <div className="absolute inset-0 w-full h-full z-20 hidden md:block">
          <motion.img 
            src="/hero-background.png" 
            alt="SmartMates for Sales" 
            className="absolute inset-0 w-full h-full opacity-100 object-cover
              object-left lg:object-right"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            style={{ 
              imageRendering: 'auto',
            }}
            onError={(e) => {
              // 画像が読み込めない場合はグラデーションを表示
              e.currentTarget.style.display = 'none';
            }}
          />
          {/* デスクトップ用波形アニメーション（滑らかな波型 - 背景画像のスマホ部分にオーバーレイ） */}
          <motion.svg
            className="absolute z-30"
            style={{
              top: '50%',
              left: '20%',
              transform: 'translate(calc(-50% - 200px), calc(-50% + 40px))',
              width: '220px',
              height: '50px',
              pointerEvents: 'none',
            }}
            viewBox="0 0 350 80"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="waveGradientDesktop" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" />
                <stop offset="50%" stopColor="#06B6D4" stopOpacity="1" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.8" />
              </linearGradient>
            </defs>
            <motion.path
              d="M0,40 Q43.75,5 87.5,40 T175,40 T262.5,40 T350,40"
              fill="none"
              stroke="url(#waveGradientDesktop)"
              strokeWidth="3"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ 
                pathLength: 1,
                opacity: [0.7, 1, 0.8, 0.7],
                d: [
                  "M0,40 Q43.75,5 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,25 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,0 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,15 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,5 87.5,40 T175,40 T262.5,40 T350,40",
                ]
              }}
              transition={{
                pathLength: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
                opacity: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
                d: { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
              }}
            />
            <motion.path
              d="M0,40 Q43.75,20 87.5,40 T175,40 T262.5,40 T350,40"
              fill="none"
              stroke="url(#waveGradientDesktop)"
              strokeWidth="2.5"
              strokeLinecap="round"
              opacity="0.7"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ 
                pathLength: 1,
                opacity: [0.5, 0.8, 0.6, 0.5],
                d: [
                  "M0,40 Q43.75,20 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,15 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,30 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,18 87.5,40 T175,40 T262.5,40 T350,40",
                  "M0,40 Q43.75,20 87.5,40 T175,40 T262.5,40 T350,40",
                ]
              }}
              transition={{
                pathLength: { duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.2 },
                opacity: { duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 },
                d: { duration: 1, repeat: Infinity, ease: "easeInOut", delay: 0.2 }
              }}
            />
          </motion.svg>
        </div>
      </div>

      {/* コンテンツ（最前面、ボタンがクリック可能） */}
      <motion.div
        className="relative z-30 max-w-full mx-auto px-4 sm:px-6 lg:px-8
          lg:max-w-5xl
          pt-8 pb-16 md:py-16 lg:py-32 w-full overflow-hidden"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* スマホ表示: テキスト → 画像 → ボタンの順 */}
        <div className="md:hidden">
          {/* テキスト（上、中央揃え） */}
          <div className="text-center px-4 mb-4">
            <motion.h1 
              className="text-2xl font-black text-white mb-2 leading-tight tracking-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            >
              {t('hero.title')}
            </motion.h1>
            <motion.p 
              className="text-sm font-bold text-blue-300"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            >
              {t('hero.subtitle')}
            </motion.p>
          </div>

          {/* スマホ画像（中央、適切なサイズ、波形アニメーション付き） */}
          <div className="flex justify-center items-center w-full mb-4">
            <div className="relative mx-auto flex justify-center">
              <img 
                src="/hero-smartphone.png" 
                alt="SmartMates for Sales" 
                className="h-auto object-contain relative z-10 block"
                style={{ 
                  imageRendering: 'auto',
                  width: '280px',
                  maxWidth: '85%',
                }}
              />
              {/* 波形アニメーション（滑らかな波型 - 実際のアプリと同じスタイル） */}
              <motion.svg
                className="absolute z-20"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, calc(-50% + 10px))',
                  width: '140px',
                  height: '30px',
                  pointerEvents: 'none',
                }}
                viewBox="0 0 250 60"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient id="waveGradientMobile" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="#06B6D4" stopOpacity="1" />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.8" />
                  </linearGradient>
                </defs>
                <motion.path
                  d="M0,30 Q31.25,5 62.5,30 T125,30 T187.5,30 T250,30"
                  fill="none"
                  stroke="url(#waveGradientMobile)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ 
                    pathLength: 1,
                    opacity: [0.7, 1, 0.8, 0.7],
                    d: [
                      "M0,30 Q31.25,5 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,20 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,0 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,12 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,5 62.5,30 T125,30 T187.5,30 T250,30",
                    ]
                  }}
                  transition={{
                    pathLength: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
                    opacity: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
                    d: { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
                  }}
                />
                <motion.path
                  d="M0,30 Q31.25,15 62.5,30 T125,30 T187.5,30 T250,30"
                  fill="none"
                  stroke="url(#waveGradientMobile)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  opacity="0.7"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ 
                    pathLength: 1,
                    opacity: [0.5, 0.8, 0.6, 0.5],
                    d: [
                      "M0,30 Q31.25,15 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,10 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,22 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,13 62.5,30 T125,30 T187.5,30 T250,30",
                      "M0,30 Q31.25,15 62.5,30 T125,30 T187.5,30 T250,30",
                    ]
                  }}
                  transition={{
                    pathLength: { duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.2 },
                    opacity: { duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 },
                    d: { duration: 1, repeat: Infinity, ease: "easeInOut", delay: 0.2 }
                  }}
                />
              </motion.svg>
            </div>
          </div>

          {/* ボタンエリア（下） */}
          <div className="flex flex-col items-center text-center">
            {/* 丸い通話開始ボタン */}
            <motion.button
              onClick={handleDemoClick}
              className="group relative bg-gradient-to-r from-blue-500 to-cyan-500 text-white w-24 h-24 rounded-full flex items-center justify-center hover:from-blue-600 hover:to-cyan-600 mb-6
                shadow-[0_10px_30px_rgba(59,130,246,0.4),0_0_15px_rgba(6,182,212,0.3),0_4px_6px_rgba(0,0,0,0.1)]"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                opacity: { duration: 0.6, delay: 0.8, ease: "easeOut" },
                scale: { duration: 0.1, ease: "easeOut" }
              }}
              whileHover={{ 
                scale: 1.1,
                transition: { delay: 0.05, duration: 0.2, ease: "easeOut" }
              }}
              whileTap={{ scale: 0.9 }}
            >
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Phone size={40} className="group-hover:scale-110 transition-transform duration-200" />
            </motion.div>
            </motion.button>

            {/* AIと今すぐ通話体験のテキスト */}
            <motion.p 
              className="text-xl font-bold text-white text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
            >
              {t('hero.cta.demo')}
            </motion.p>
            
            {/* テキストチャットを試すリンク */}
            <motion.a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleTextChatClick();
              }}
              className="text-white text-sm underline mt-2 hover:text-blue-300 transition-colors block text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
            >
              {t('hero.cta.textChat')}
            </motion.a>
          </div>
        </div>

        {/* デスクトップ表示: 通常のレイアウト */}
        <div className="hidden md:block text-center">
          <motion.h1 
            className="text-5xl lg:text-7xl font-black text-white mb-6 lg:mb-8 leading-tight tracking-tight
              lg:max-w-none"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            {t('hero.title')}
          </motion.h1>

          <motion.p 
            className="text-2xl lg:text-3xl font-bold text-blue-300 mb-12 lg:mb-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* フォーム入力不要のテキスト */}
          <motion.p 
            className="text-lg text-gray-300 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
          >
            {t('hero.noForm')}
          </motion.p>

          {/* 丸い通話開始ボタン */}
          <motion.button
            onClick={handleDemoClick}
            className="group relative bg-gradient-to-r from-blue-500 to-cyan-500 text-white w-32 h-32 lg:w-36 lg:h-36 rounded-full flex items-center justify-center hover:from-blue-600 hover:to-cyan-600 mx-auto mb-6
              shadow-[0_15px_40px_rgba(59,130,246,0.5),0_0_20px_rgba(6,182,212,0.4),0_6px_8px_rgba(0,0,0,0.15)]"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ 
              opacity: { duration: 0.6, delay: 0.8, ease: "easeOut" },
              scale: { duration: 0.1, ease: "easeOut" }
            }}
            whileHover={{ 
              scale: 1.1,
              transition: { delay: 0.05, duration: 0.2, ease: "easeOut" }
            }}
            whileTap={{ scale: 0.9 }}
          >
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <Phone size={64} className="lg:w-20 lg:h-20 group-hover:scale-110 transition-transform duration-200" />
            </motion.div>
          </motion.button>

          {/* AIと今すぐ通話体験のテキスト */}
          <motion.p 
            className="text-2xl font-bold text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
          >
            {t('hero.cta.demo')}
          </motion.p>
          
          {/* テキストチャットを試すリンク */}
          <motion.a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleTextChatClick();
            }}
            className="text-white text-base underline mt-3 hover:text-blue-300 transition-colors block text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
          >
            {t('hero.cta.textChat')}
          </motion.a>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;

